import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, X } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { Reveal } from "@/hooks/useReveal";
import { BLOG_POSTS, BLOG_CATEGORIES } from "@/content/blog";
import { LeadMagnetForm } from "@/components/site/LeadMagnetForm";
import { cn } from "@/lib/utils";

type Filter = "All" | (typeof BLOG_CATEGORIES)[number];

const Blog = () => {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("All");

  const filteredPosts = useMemo(() => {
    const q = query.trim().toLowerCase();
    return BLOG_POSTS.filter((p) => {
      if (filter !== "All" && p.category !== filter) return false;
      if (!q) return true;
      const haystack = [
        p.title,
        p.description,
        p.hook,
        p.category,
        p.eyebrow,
        ...(p.keywords ?? []),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [query, filter]);

  const filters: Filter[] = ["All", ...BLOG_CATEGORIES];
  const countsByCategory = useMemo(() => {
    const map: Record<string, number> = { All: BLOG_POSTS.length };
    for (const c of BLOG_CATEGORIES) {
      map[c] = BLOG_POSTS.filter((p) => p.category === c).length;
    }
    return map;
  }, []);


  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="Insights — Leadership, Speaking & Books | Dr. Sully"
        description="Long-form essays from Dr. RD 'Sully' Sullivan on executive leadership, keynote speaking, and the books behind the MAKECHANGE framework."
        path="/blog"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "DrSullySpeaks — Insights",
          url: "https://drsullyspeaks.com/blog",
          author: {
            "@type": "Person",
            name: "Dr. RD 'Sully' Sullivan",
            url: "https://drsullyspeaks.com/about",
          },
          blogPost: BLOG_POSTS.map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            description: p.description,
            url: `https://drsullyspeaks.com/blog/${p.slug}`,
            datePublished: p.publishedISO,
            author: { "@type": "Person", name: "Dr. RD 'Sully' Sullivan" },
          })),
        }}
      />
      <SiteNav />

      <main id="main">
        {/* HERO */}
        <section className="relative pt-40 md:pt-48 pb-16 md:pb-20 bg-hero overflow-hidden">
          <div className="container max-w-4xl">
            <Reveal variant="up">
              <span className="eyebrow">Insights</span>
              <h1 className="font-display font-bold uppercase text-white text-4xl md:text-6xl lg:text-7xl mt-4 leading-[1.05]">
                Long-form for<br />
                <span className="text-gradient-gold">leaders who read</span>.
              </h1>
              <p className="text-silver text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
                Essays from Dr. Sully on executive leadership, keynote speaking,
                and the books behind the MAKECHANGE framework. No listicles.
                No thin takes. Bring a pen.
              </p>
            </Reveal>
          </div>
        </section>

        {/* SEARCH + FILTERS */}
        <section className="border-t border-border bg-navy-deep/60 sticky top-0 z-30 backdrop-blur-md">
          <div className="container max-w-5xl py-5 md:py-6">
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
              <label className="relative flex-1 group">
                <span className="sr-only">Search essays</span>
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-silver/70 group-focus-within:text-gold transition-colors"
                  aria-hidden
                />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search essays, topics, keywords…"
                  className="w-full bg-navy border border-border pl-10 pr-10 py-3 text-sm text-white placeholder:text-silver/50 focus:outline-none focus:border-gold/60 focus:ring-2 focus:ring-gold/40 transition-all"
                  aria-label="Search essays"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-silver/70 hover:text-white transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </label>

              <div
                role="tablist"
                aria-label="Filter by category"
                className="flex flex-wrap gap-2"
              >
                {filters.map((f) => {
                  const active = filter === f;
                  return (
                    <button
                      key={f}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setFilter(f)}
                      className={cn(
                        "px-4 py-2 text-[11px] uppercase tracking-[0.2em] border transition-all",
                        active
                          ? "bg-gold text-navy border-gold"
                          : "border-border text-silver hover:text-white hover:border-gold/60",
                      )}
                    >
                      {f}
                      <span
                        className={cn(
                          "ml-2 text-[10px]",
                          active ? "text-navy/70" : "text-silver/60",
                        )}
                      >
                        {countsByCategory[f] ?? 0}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* RESULTS */}
        <section className="py-12 md:py-16 border-t border-border">
          <div className="container max-w-5xl">
            <div
              className="flex items-baseline justify-between mb-8"
              aria-live="polite"
            >
              <p className="text-xs uppercase tracking-[0.25em] text-silver">
                {filteredPosts.length} {filteredPosts.length === 1 ? "essay" : "essays"}
                {filter !== "All" && (
                  <>
                    {" "}· <span className="text-gold">{filter}</span>
                  </>
                )}
                {query && (
                  <>
                    {" "}· matching "<span className="text-white">{query}</span>"
                  </>
                )}
              </p>
              {(query || filter !== "All") && (
                <button
                  type="button"
                  onClick={() => {
                    setQuery("");
                    setFilter("All");
                  }}
                  className="text-[10px] uppercase tracking-[0.25em] text-gold hover:text-white transition-colors"
                >
                  Reset
                </button>
              )}
            </div>

            {filteredPosts.length === 0 ? (
              <div className="border border-border bg-navy p-10 md:p-14 text-center">
                <h3 className="font-display uppercase text-white text-xl md:text-2xl">
                  No essays match.
                </h3>
                <p className="text-silver text-sm mt-3 max-w-md mx-auto leading-relaxed">
                  Try a broader keyword — or clear the filter and browse everything.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setQuery("");
                    setFilter("All");
                  }}
                  className="mt-6 inline-flex items-center gap-2 border border-gold/50 text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-3 hover:bg-gold/10 hover:border-gold transition-all"
                >
                  Show all essays
                </button>
              </div>
            ) : (
              <ul className="grid md:grid-cols-2 gap-px bg-border border border-border">
                {filteredPosts.map((p) => (
                  <li key={p.slug} className="bg-navy hover:bg-navy-elevated transition-colors">
                    <Link
                      to={`/blog/${p.slug}`}
                      className="block p-8 md:p-10 h-full group focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/70 focus-visible:ring-offset-2 focus-visible:ring-offset-navy"
                    >
                      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-silver/80">
                        <span className="text-gold">{p.category}</span>
                        <span>·</span>
                        <span>{p.readMinutes} min read</span>
                      </div>
                      <h3 className="font-display uppercase text-white text-xl md:text-2xl mt-4 leading-tight group-hover:text-gradient-gold transition-all">
                        {p.title}
                      </h3>
                      <p className="text-silver text-sm md:text-base mt-4 leading-relaxed">
                        {p.description}
                      </p>
                      <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold">
                        Read essay
                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>


        {/* NEWSLETTER CAPTURE */}
        <section className="py-20 md:py-24 border-t border-border">
          <div className="container max-w-2xl">
            <Reveal variant="up">
              <LeadMagnetForm sourcePage="blog-index" />
            </Reveal>
          </div>
        </section>



        {/* CLOSING CTA */}
        <section className="py-24 border-t border-border bg-navy-deep">
          <div className="container max-w-3xl text-center">
            <Reveal variant="up">
              <span className="eyebrow !mx-auto">Next Step</span>
              <h2 className="font-display uppercase text-white text-3xl md:text-5xl mt-4 leading-tight">
                Done reading?<br />
                <span className="text-gradient-gold">Bring the change.</span>
              </h2>
              <p className="text-silver mt-5 max-w-xl mx-auto leading-relaxed">
                Every essay here is drawn from real coaching engagements.
                If the frameworks are landing, the fastest way forward is
                a private conversation.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/inquire"
                  className="inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-sm px-7 py-5 shadow-cta hover:brightness-110 transition-all"
                >
                  Work With Dr. Sully
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/books"
                  className="inline-flex items-center justify-center gap-2 border border-gold/50 text-white font-display uppercase tracking-[0.2em] text-sm px-7 py-5 hover:bg-gold/10 hover:border-gold transition-all"
                >
                  Browse the Books
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default Blog;
